import { Fragment, useEffect, useRef } from 'react'
import { Tabs as ArkTabs } from '@ark-ui/react'
import { cn } from '../../lib/cn'
import { tabList, tabTrigger } from './variants'

export interface TabDef<K extends string> {
  key: K
  label: string
  /** Count shown after the label. Omitted or 0 renders nothing. */
  badge?: number
}

/**
 * The app's tab strip, on Ark UI — real `tablist` ARIA with roving arrow-key, Home and End focus.
 *
 * Ported from the sibling project rather than written again, so the two stay one house style and so
 * the keyboard behaviour is the library's problem instead of ours. Renders the TRIGGER ROW only:
 * every consumer owns its own state and panel, because the state usually belongs in the URL and
 * there is then nothing left for this to hold.
 *
 * **The horizontal scroll is not optional.** The segmented list keeps `overflow-hidden` so the
 * rounded border clips the end triggers' square corners — which also means a row wider than its
 * container is silently CLIPPED rather than scrolled, and on a phone the tabs at the end simply
 * become unreachable. The wrapper owns the scroll so the pill keeps its clip and every tab stays
 * reachable; callers must not hand-wrap it.
 */
export function Tabs<K extends string>({
  active,
  onChange,
  tabs,
  variant = 'segmented',
  ariaLabel,
  className,
}: {
  active: K
  onChange: (tab: K) => void
  tabs: readonly TabDef<K>[]
  variant?: 'segmented' | 'underline'
  ariaLabel?: string
  className?: string
}) {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Bring the selected tab into view. A default that lands mid-strip would otherwise sit off the
  // right edge on a phone. `block: 'nearest'` keeps this from scrolling the page vertically.
  useEffect(() => {
    scrollRef.current
      ?.querySelector<HTMLElement>('[data-selected]')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [active])

  return (
    <div ref={scrollRef} className={cn('max-w-full overflow-x-auto', className)}>
      <ArkTabs.Root
        className="inline-flex"
        value={active}
        onValueChange={(details) => onChange(details.value as K)}
      >
        <ArkTabs.List aria-label={ariaLabel} className={tabList({ variant })}>
          {tabs.map((tab) => (
            <Fragment key={tab.key}>
              <ArkTabs.Trigger value={tab.key} className={tabTrigger({ variant })}>
                {tab.label}
                {(tab.badge ?? 0) > 0 && (
                  <span className="tabular rounded-full bg-surface-raised px-1.5 py-0.5 text-xs font-semibold text-ink-faint">
                    {tab.badge}
                  </span>
                )}
              </ArkTabs.Trigger>
            </Fragment>
          ))}
        </ArkTabs.List>
      </ArkTabs.Root>
    </div>
  )
}
